const parseNumeric = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const normalized = raw.replace(/,/g, '').replace(/[^\d.+\-*/() ]/g, '');
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

const formatCalcResult = (value: number): string => {
  if (!Number.isFinite(value)) return '';
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return rounded.toLocaleString('en-IN');
  return rounded.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const normalizeTokenKey = (raw: unknown): string => {
  return String(raw ?? '')
    .replace(/^\{\{\s*/, '')
    .replace(/\s*\}\}$/, '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
};

const buildIdentifierValueMap = (replacements: Record<string, string>) => {
  const map = new Map<string, number>();
  Object.entries(replacements || {}).forEach(([token, value]) => {
    const numeric = parseNumeric(value);
    if (numeric == null) return;
    const simple = normalizeTokenKey(token);
    if (simple) map.set(simple, numeric);
  });
  return map;
};

const safeEvaluate = (expression: string): number | null => {
  const source = String(expression || '').trim();
  if (!source) return null;
  if (!/^[0-9+\-*/().\s]+$/.test(source)) return null;
  try {
    // expression is restricted to numbers/operators/parentheses above.
    // eslint-disable-next-line no-new-func
    const fn = new Function(`return (${source});`);
    const value = Number(fn());
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
};

export const resolveTemplateCalcs = (input: string, replacements: Record<string, string>) => {
  const idValues = buildIdentifierValueMap(replacements);
  return String(input || '').replace(/\{\{\s*calc\s*:\s*([^}]+)\}\}/gi, (_full, expr) => {
    let expression = String(expr || '');
    Object.entries(replacements || {}).forEach(([token, value]) => {
      expression = expression.split(token).join(String(value ?? ''));
    });
    expression = expression.replace(/[A-Za-z_][A-Za-z0-9_]*/g, (name) => {
      const key = String(name || '').toLowerCase();
      if (idValues.has(key)) return String(idValues.get(key));
      return '0';
    });
    const value = safeEvaluate(expression);
    if (value == null) return '';
    return formatCalcResult(value);
  });
};

const placeRowTokenInsideNextTable = (input: string, tokenName: 'payments_rows' | 'gst_rows') => {
  const tokenPattern = new RegExp(`\\{\\{\\s*${tokenName}\\s*\\}\\}`, 'i');
  let output = String(input || '');
  let match = tokenPattern.exec(output);
  while (match) {
    const tokenStart = match.index;
    const tokenText = match[0];
    const previousTableStart = output.lastIndexOf('<table', tokenStart);
    const previousTableEnd = output.lastIndexOf('</table>', tokenStart);
    if (previousTableStart > previousTableEnd) {
      match = tokenPattern.exec(output.slice(tokenStart + tokenText.length));
      if (match) match.index += tokenStart + tokenText.length;
      continue;
    }

    const nextTableStart = output.indexOf('<table', tokenStart + tokenText.length);
    if (nextTableStart < 0) break;
    const nextTableEnd = output.indexOf('</table>', nextTableStart);
    if (nextTableEnd < 0) break;

    const withoutToken = `${output.slice(0, tokenStart)}${output.slice(tokenStart + tokenText.length)}`;
    const adjustedTableStart = nextTableStart - tokenText.length;
    const adjustedTableEnd = nextTableEnd - tokenText.length;
    let searchFrom = adjustedTableStart;
    let insertAt = -1;
    for (let i = 0; i < 2; i += 1) {
      const rowEnd = withoutToken.indexOf('</tr>', searchFrom);
      if (rowEnd < 0 || rowEnd > adjustedTableEnd) break;
      insertAt = rowEnd + '</tr>'.length;
      searchFrom = insertAt;
    }
    if (insertAt < 0) break;
    output = `${withoutToken.slice(0, insertAt)}\n${tokenText}\n${withoutToken.slice(insertAt)}`;
    match = tokenPattern.exec(output.slice(insertAt + tokenText.length));
    if (match) match.index += insertAt + tokenText.length;
  }
  return output;
};

export const applyTemplateTokens = (input: string, replacements: Record<string, string>) => {
  const rowTokenPattern = /\{\{\s*(payments_rows|gst_rows)\s*\}\}/i;
  let output = String(input || '')
    .replace(/&#123;|&lbrace;/gi, '{')
    .replace(/&#125;|&rbrace;/gi, '}')
    .replace(/<!--\s*(\{\{\s*(payments_rows|gst_rows)\s*\}\})\s*-->/gi, '$1')
    .replace(
      /<(p|div|span)[^>]*>\s*(\{\{\s*(payments_rows|gst_rows)\s*\}\})\s*<\/\1>/gi,
      '$2'
    );
  output = placeRowTokenInsideNextTable(output, 'payments_rows');
  output = placeRowTokenInsideNextTable(output, 'gst_rows');
  output = resolveTemplateCalcs(output, replacements);
  Object.entries(replacements || {}).forEach(([token, value]) => {
    output = output.split(token).join(String(value ?? ''));
  });
  const normalizedValueMap = new Map<string, string>();
  Object.entries(replacements || {}).forEach(([token, value]) => {
    const simple = normalizeTokenKey(token);
    if (simple) normalizedValueMap.set(simple, String(value ?? ''));
  });
  output = output.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (full, rawKey) => {
    if (String(rawKey || '').includes(':')) return full;
    const key = normalizeTokenKey(rawKey);
    if (!key) return full;
    return normalizedValueMap.has(key) ? String(normalizedValueMap.get(key) || '') : full;
  });
  if (rowTokenPattern.test(String(input || ''))) {
    output = output.replace(
      /<(p|div|span)[^>]*>\s*((?:<tr[\s\S]*?<\/tr>\s*)+)<\/\1>/gi,
      '$2'
    );
  }
  return output;
};
